import React, { useEffect, useState } from "react";
import { format } from "date-fns";
import {
  getMyVacationRequests,
  getEmployeeVacationRequests,
  cancelVacationRequest,
  createVacationRequest,
  approveVacationRequest,
  rejectVacationRequest,
  adminCreateVacationEntry,
  adminUpdateVacationRequest,
  adminDeleteVacationRequest,
} from "../services/apiService";
import { FaEdit, FaCheck, FaTimes } from "react-icons/fa";
import { MdDelete, MdCancel } from "react-icons/md";
import { VacationRequest, VacationRequestStatus } from "../types";
import ActionButton from "./ActionButton";
import RejectReasonModal from "./RejectReasonModal";
import DateRangePicker from "./DateRangePicker";

interface VacationRequestListProps {
  onRequestSelect?: (request: VacationRequest) => void;
  refreshTrigger?: number; // Optional prop to trigger refresh from parent
  showAddButton?: boolean; // Whether the employee can submit a new request
  employeeUserId?: string; // Optional: show requests for a specific employee
  isAdmin?: boolean; // Enables admin actions (approve/reject/edit/delete and manual entry)
  onDataChange?: () => void; // Notifies the parent when a change affects the vacation balance
}

interface EditableValues {
  startDate: string;
  endDate: string;
  notes: string;
}

const EMPTY_VALUES: EditableValues = { startDate: "", endDate: "", notes: "" };

const VacationRequestList: React.FC<VacationRequestListProps> = ({
  onRequestSelect,
  refreshTrigger,
  showAddButton = true,
  employeeUserId,
  isAdmin = false,
  onDataChange,
}) => {
  const [requests, setRequests] = useState<VacationRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cancellingId, setCancellingId] = useState<number | null>(null);

  // Inline add state
  const [isAdding, setIsAdding] = useState(false);
  const [newRequest, setNewRequest] = useState<EditableValues>(EMPTY_VALUES);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  // Admin action state
  const [processingId, setProcessingId] = useState<number | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editValues, setEditValues] = useState<EditableValues>(EMPTY_VALUES);
  const [rejectingRequest, setRejectingRequest] = useState<VacationRequest | null>(null);

  const getTodayString = () => new Date().toISOString().split("T")[0];

  const fetchRequests = async () => {
    try {
      setLoading(true);
      setError(null);
      // If employeeUserId is provided, fetch that employee's requests, otherwise fetch current user's requests
      const data = employeeUserId
        ? await getEmployeeVacationRequests(employeeUserId)
        : await getMyVacationRequests();

      if (Array.isArray(data)) {
        setRequests(data);
      } else {
        console.error("Expected array but got:", typeof data, data);
        setRequests([]);
        setError("Invalid data format received from server.");
      }
    } catch (err: any) {
      console.error("Error fetching vacation requests:", err);
      setError("Fehler beim Laden der Urlaubsanträge. Bitte erneut versuchen.");
      setRequests([]);
    } finally {
      setLoading(false);
    }
  };

  // Reloads the list and notifies the parent so dependent views (e.g. the detailed
  // statistics table) reflect the changed vacation balance.
  const refreshAfterChange = async () => {
    await fetchRequests();
    onDataChange?.();
  };

  useEffect(() => {
    // Small delay to ensure authentication is ready
    const timer = setTimeout(() => {
      fetchRequests();
    }, 100);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTrigger, employeeUserId]);

  const handleCancel = async (requestId: number) => {
    if (!window.confirm("Möchtest du diesen Urlaubsantrag wirklich stornieren?")) {
      return;
    }

    setCancellingId(requestId);
    try {
      await cancelVacationRequest(requestId);
      await refreshAfterChange();
    } catch (err: any) {
      alert(err.response?.data?.message || "Fehler beim Stornieren des Urlaubsantrags");
    } finally {
      setCancellingId(null);
    }
  };

  const handleAddNew = () => {
    setIsAdding(true);
    setAddError(null);
    setNewRequest(EMPTY_VALUES);
  };

  const handleCancelAdd = () => {
    setIsAdding(false);
    setAddError(null);
    setNewRequest(EMPTY_VALUES);
  };

  const validateDates = (values: EditableValues, allowPast: boolean): string | null => {
    if (!values.startDate || !values.endDate) {
      return "Bitte beide Datumsfelder auswählen";
    }
    if (new Date(values.startDate) > new Date(values.endDate)) {
      return "Startdatum muss vor oder gleich dem Enddatum sein";
    }
    if (!allowPast && new Date(values.startDate) < new Date(getTodayString())) {
      return "Urlaub kann nicht für vergangene Daten beantragt werden";
    }
    return null;
  };

  const handleSubmitNew = async () => {
    const validationError = validateDates(newRequest, isAdmin);
    if (validationError) {
      setAddError(validationError);
      return;
    }

    setAddError(null);
    setIsSubmitting(true);
    try {
      if (isAdmin && employeeUserId) {
        await adminCreateVacationEntry(
          employeeUserId,
          newRequest.startDate,
          newRequest.endDate,
          newRequest.notes || undefined
        );
      } else {
        await createVacationRequest(
          newRequest.startDate,
          newRequest.endDate,
          newRequest.notes || undefined
        );
      }

      setIsAdding(false);
      setNewRequest(EMPTY_VALUES);
      await refreshAfterChange();
    } catch (err: any) {
      setAddError(err.response?.data?.message || "Urlaubseintrag konnte nicht gespeichert werden");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleApprove = async (request: VacationRequest) => {
    setProcessingId(request.id);
    try {
      await approveVacationRequest(request.id);
      await refreshAfterChange();
    } catch (err: any) {
      alert(err.response?.data?.message || "Fehler beim Genehmigen des Urlaubsantrags");
    } finally {
      setProcessingId(null);
    }
  };

  const handleRejectSubmit = async (reason: string) => {
    if (!rejectingRequest) {
      return;
    }
    setProcessingId(rejectingRequest.id);
    try {
      await rejectVacationRequest(rejectingRequest.id, reason);
      setRejectingRequest(null);
      await refreshAfterChange();
    } catch (err: any) {
      alert(err.response?.data?.message || "Fehler beim Ablehnen des Urlaubsantrags");
    } finally {
      setProcessingId(null);
    }
  };

  const handleEditStart = (request: VacationRequest) => {
    setEditingId(request.id);
    setEditValues({
      startDate: request.startDate,
      endDate: request.endDate,
      notes: request.notes || "",
    });
    setAddError(null);
  };

  const handleEditCancel = () => {
    setEditingId(null);
    setEditValues(EMPTY_VALUES);
  };

  const handleEditSave = async (requestId: number) => {
    const validationError = validateDates(editValues, true);
    if (validationError) {
      alert(validationError);
      return;
    }

    setProcessingId(requestId);
    try {
      await adminUpdateVacationRequest(
        requestId,
        editValues.startDate,
        editValues.endDate,
        editValues.notes || undefined
      );
      setEditingId(null);
      await refreshAfterChange();
    } catch (err: any) {
      alert(err.response?.data?.message || "Fehler beim Bearbeiten des Urlaubseintrags");
    } finally {
      setProcessingId(null);
    }
  };

  const handleDelete = async (requestId: number) => {
    if (!window.confirm("Möchtest du diesen Urlaubseintrag wirklich löschen?")) {
      return;
    }
    setProcessingId(requestId);
    try {
      await adminDeleteVacationRequest(requestId);
      await refreshAfterChange();
    } catch (err: any) {
      alert(err.response?.data?.message || "Fehler beim Löschen des Urlaubseintrags");
    } finally {
      setProcessingId(null);
    }
  };

  const getStatusBadgeStyle = (status: VacationRequestStatus) => {
    const baseStyle = {
      padding: "4px 8px",
      borderRadius: "4px",
      fontSize: "0.875rem",
      fontWeight: "bold",
    };

    switch (status) {
      case VacationRequestStatus.PENDING:
        return { ...baseStyle, backgroundColor: "#fff3cd", color: "#856404" };
      case VacationRequestStatus.APPROVED:
        return { ...baseStyle, backgroundColor: "#d4edda", color: "#155724" };
      case VacationRequestStatus.REJECTED:
        return { ...baseStyle, backgroundColor: "#f8d7da", color: "#721c24" };
      case VacationRequestStatus.CANCELLED:
        return { ...baseStyle, backgroundColor: "#e2e3e5", color: "#383d41" };
      default:
        return baseStyle;
    }
  };

  const renderDateInputs = (
    values: EditableValues,
    onChange: (values: EditableValues) => void,
    restrictPast: boolean
  ) => (
    <DateRangePicker
      startDate={values.startDate}
      endDate={values.endDate}
      restrictPast={restrictPast}
      onChange={(range) => onChange({ ...values, ...range })}
    />
  );

  const renderEditableRow = (
    values: EditableValues,
    onChange: (values: EditableValues) => void,
    onSave: () => void,
    onCancel: () => void,
    saving: boolean,
    statusCell: React.ReactNode,
    requestedCell: React.ReactNode,
    restrictPast: boolean
  ) => (
    <tr style={{ backgroundColor: "#f8f9fa", border: "2px solid #007bff" }}>
      <td>{renderDateInputs(values, onChange, restrictPast)}</td>
      <td>{statusCell}</td>
      <td>{requestedCell}</td>
      <td>
        <textarea
          value={values.notes}
          onChange={(e) => onChange({ ...values, notes: e.target.value })}
          placeholder="Optionale Notizen..."
          style={{ width: "100%", minHeight: "40px", fontSize: "0.875rem", resize: "vertical" }}
        />
      </td>
      <td className="actions">
        <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
          <ActionButton onClick={onSave} disabled={saving}>
            {saving ? "Speichert..." : "Speichern"}
          </ActionButton>
          <ActionButton onClick={onCancel} disabled={saving} variant="secondary">
            Abbrechen
          </ActionButton>
        </div>
      </td>
    </tr>
  );

  const renderRowActions = (request: VacationRequest) => {
    const busy = processingId === request.id;

    if (isAdmin) {
      return (
        <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
          {request.status === VacationRequestStatus.PENDING && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); handleApprove(request); }}
                disabled={busy}
                className="delete-icon-button"
                title="Genehmigen"
                style={{ backgroundColor: "#2e7d32" }}
              >
                {FaCheck({})}
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); setRejectingRequest(request); }}
                disabled={busy}
                className="delete-icon-button"
                title="Ablehnen"
              >
                {FaTimes({})}
              </button>
            </>
          )}
          <button
            onClick={(e) => { e.stopPropagation(); handleEditStart(request); }}
            disabled={busy}
            className="delete-icon-button"
            title="Bearbeiten"
            style={{ backgroundColor: "var(--secondary-color)" }}
          >
            {FaEdit({})}
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); handleDelete(request.id); }}
            disabled={busy}
            className="delete-icon-button"
            title="Löschen"
          >
            {MdDelete({})}
          </button>
        </div>
      );
    }

    const canCancel =
      request.status === VacationRequestStatus.PENDING ||
      (request.status === VacationRequestStatus.APPROVED && new Date(request.startDate) > new Date());

    if (!canCancel) {
      return null;
    }

    return (
      <button
        onClick={(e) => { e.stopPropagation(); handleCancel(request.id); }}
        disabled={cancellingId === request.id}
        className="delete-icon-button"
        title="Stornieren"
        style={{ backgroundColor: "var(--secondary-color)" }}
      >
        {MdCancel({})}
      </button>
    );
  };

  if (loading) {
    return <p>Lädt...</p>;
  }

  if (error) {
    return (
      <div style={{ backgroundColor: "#ffebee", border: "1px solid #f44336", color: "#c62828", padding: "10px", borderRadius: "4px" }}>
        {error}
        <button onClick={fetchRequests} style={{ marginLeft: "10px", cursor: "pointer" }}>
          Erneut versuchen
        </button>
      </div>
    );
  }

  const canAdd = isAdmin || showAddButton;
  const addLabel = isAdmin ? "+ Urlaub eintragen" : "+ Neuer Antrag";

  const addStatusCell = (
    <span style={getStatusBadgeStyle(isAdmin ? VacationRequestStatus.APPROVED : VacationRequestStatus.PENDING)}>
      {isAdmin ? VacationRequestStatus.APPROVED : VacationRequestStatus.PENDING}
    </span>
  );
  const addRequestedCell = <span style={{ fontSize: "0.875rem", color: "#6c757d" }}>Jetzt</span>;

  return (
    <>
      <div className="section-header">
        <h2>Urlaubsanträge</h2>
        {canAdd && (
          <ActionButton onClick={handleAddNew} disabled={isAdding}>
            {addLabel}
          </ActionButton>
        )}
      </div>

      {addError && (
        <div style={{ backgroundColor: "#ffebee", border: "1px solid #f44336", color: "#c62828", padding: "10px", borderRadius: "4px", marginBottom: "10px" }}>
          {addError}
        </div>
      )}

      {requests.length === 0 && !isAdding ? (
        <p>Keine Urlaubsanträge gefunden.</p>
      ) : (
        <div className="table-wrapper">
          <table className="history-table">
            <thead>
              <tr>
                <th>Zeitraum</th>
                <th>Status</th>
                <th>Beantragt am</th>
                <th>Notizen</th>
                <th className="actions">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {isAdding &&
                renderEditableRow(
                  newRequest,
                  setNewRequest,
                  handleSubmitNew,
                  handleCancelAdd,
                  isSubmitting,
                  addStatusCell,
                  addRequestedCell,
                  !isAdmin
                )}
              {requests.map((request) =>
                editingId === request.id ? (
                  <React.Fragment key={request.id}>
                    {renderEditableRow(
                      editValues,
                      setEditValues,
                      () => handleEditSave(request.id),
                      handleEditCancel,
                      processingId === request.id,
                      <span style={getStatusBadgeStyle(request.status)}>{request.status}</span>,
                      <span>{format(new Date(request.requestDate), "dd.MM.yyyy")}</span>,
                      false
                    )}
                  </React.Fragment>
                ) : (
                  <tr
                    key={request.id}
                    onClick={() => onRequestSelect?.(request)}
                    style={{ cursor: onRequestSelect ? "pointer" : "default" }}
                  >
                    <td>
                      {format(new Date(request.startDate), "dd.MM.yyyy")} -{" "}
                      {format(new Date(request.endDate), "dd.MM.yyyy")}
                    </td>
                    <td>
                      <span style={getStatusBadgeStyle(request.status)}>{request.status}</span>
                    </td>
                    <td>{format(new Date(request.requestDate), "dd.MM.yyyy")}</td>
                    <td>
                      {request.notes || "-"}
                      {request.status === VacationRequestStatus.REJECTED && request.rejectionReason && (
                        <div style={{ color: "#721c24", fontSize: "0.875rem", marginTop: "4px" }}>
                          <strong>Grund:</strong> {request.rejectionReason}
                        </div>
                      )}
                    </td>
                    <td className="actions">{renderRowActions(request)}</td>
                  </tr>
                )
              )}
            </tbody>
          </table>
        </div>
      )}

      {rejectingRequest && (
        <RejectReasonModal
          request={rejectingRequest}
          saving={processingId === rejectingRequest.id}
          onSubmit={handleRejectSubmit}
          onCancel={() => setRejectingRequest(null)}
        />
      )}
    </>
  );
};

export default VacationRequestList;
